import React, {useCallback, useEffect, useState} from 'react'
import {Tab, Tabs} from '@mui/material';
import {TabContext} from "@mui/lab";
import SubmittedCorrectionsTab
  from '../../../components/inventory-correction/buyer-inventory-correction-tabs/SubmittedCorrectionsTab';
import ReviewAndCorrectTab
  from '../../../components/inventory-correction/buyer-inventory-correction-tabs/ReviewAndCorrectTab';
import ReviewModal from '../../../components/inventory-correction/ReviewModal';
import {
  deleteInventoryCorrection,
  searchBuyerCorrectionReview,
  searchBuyerInventoryCorrections,
  submitInventoryCorrection
} from '../../../components/inventory-correction/requests/inventory-correction-requests';
import {
  BuyerInventoryCorrectionTabKeys,
  BuyerInventoryCorrectionTabs,
  formatTabHeader
} from '../../../components/inventory-correction/data';
import {DateFormats} from '../../../components/constants/securspace-constants';
import {getErrorMessageForNonStandardAndStandardResponse} from '../../../util/NetworkErrorUtil';
import SubmitCorrectionDialog from '../../../components/daily-activity/SubmitCorrectionDialog';
import {withSnackbar} from "../../../components/hocs/withSnackbar";
import moment from 'moment';
import {BuyerInventoryTabPanel} from './styles';
import useUrlQueryParams from "../../../hooks/useUrlQueryParams";
import {SecurSpaceContainer, SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../../components/common";
import {TabContextContainer} from "../../../components/common/reports/tabs/TabContextContainer";
import {TabsContainer} from "../../../components/common/reports/tabs/TabsContainer";

export const modalTypeOptions = {REVIEW: 'REVIEW', SUBMIT: 'SUBMIT'};

const BuyerInventoryCorrections = ({ account, snackbarShowMessage }) => {
  const { tab: initialTab, orderNumber: initialOrderNumber, startDate: paramStartDate, endDate: paramEndDate, locationId: initialLocationId, equipmentNumber: equipmentNumber } = useUrlQueryParams();
  const initialStartDate = paramStartDate && moment(paramStartDate).isValid()
      ? moment(paramStartDate).format(DateFormats.DAY).toString()
      : moment().add(-1, 'months').format(DateFormats.DAY).toString();
  const initialEndDate = paramEndDate && moment(paramEndDate).isValid()
      ? moment(paramEndDate).format(DateFormats.DAY).toString()
      : moment().format(DateFormats.DAY).toString();

  const initialPage = 0;
  const initialSize = 10;

  const [correctionToReview, setCorrectionToReview] = useState(null);
  const [inventoryToCorrect, setInventoryToCorrect] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [currentTab, setCurrentTab] = useState(
    Object.values(BuyerInventoryCorrectionTabKeys).includes(initialTab)
      ? initialTab : BuyerInventoryCorrectionTabKeys.SUBMITTED
  );
  const [rawCorrections, setRawCorrections] = useState([]);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  const [totalNumberInventoryRecords, setTotalNumberInventoryRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(initialPage);
  const [size, setSize] = useState(initialSize);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const initialSort = {value: '', direction: 'desc'};
  const [submittedCorrectionSort, setSubmittedCorrectionSort] = useState(initialSort);
  const [reviewAndCorrectSort, setReviewAndCorrectSort] = useState(initialSort);

  const initialSubmittedCorrectionsQuery = {
      sortDir: submittedCorrectionSort.direction,
      sortBy: submittedCorrectionSort.value,
      locationId: initialLocationId || ''
  };

  const initialInventoryRecordsQuery = {
      startDate,
      endDate,
      sortDir: reviewAndCorrectSort.direction,
      sortBy: reviewAndCorrectSort.value,
      page,
      size,
      orderNumber: initialOrderNumber || '',
      locationId: initialLocationId || '',
      equipmentNumber: equipmentNumber || ''
  };

  const [submittedCorrectionsQuery, setSubmittedCorrectionsQuery] = useState(initialSubmittedCorrectionsQuery);
  const [inventoryRecordsQuery, setInventoryRecordsQuery] = useState(initialInventoryRecordsQuery);

  const filterInventoryWithTwoResolvedCorrections = (inventoryRecords) => {
    const resolvedStatuses = ['APPROVED', 'REJECTED'];
    const filteredRecords = inventoryRecords.filter(item => {
      const checkInCorrectionResolved = resolvedStatuses.includes(item.checkInCorrectionStatus);
      const checkOutCorrectionResolved = resolvedStatuses.includes(item.checkOutCorrectionStatus);
      return !(checkInCorrectionResolved && checkOutCorrectionResolved);
    });
    return filteredRecords
  };

  const fetchInventoryRecords = useCallback(() => {
    setLoading(true)
    searchBuyerCorrectionReview(inventoryRecordsQuery)
    .then(({body}) => {
        setInventoryRecords(filterInventoryWithTwoResolvedCorrections(body.content));
        setTotalNumberInventoryRecords(body.count)
        setLoading(false)
    }).catch(err => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error");
    }).finally(() => setLoading(false))

  }, [snackbarShowMessage, inventoryRecordsQuery]);

  const fetchSubmittedCorrections = useCallback(() => {
    setLoading(true);

    searchBuyerInventoryCorrections(account.id, submittedCorrectionsQuery)
      .then(({body}) => {
        setRawCorrections(body);
      })
      .catch((error) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error');
      }).finally(() => setLoading(false))

  }, [account, snackbarShowMessage, submittedCorrectionsQuery]);

  const handleOpenReviewModal = () => setModalType(modalTypeOptions.REVIEW);

  const handleCloseModal = () => {
    setModalType();
  };

  const resetAllFilters = () => {
    setPage(initialPage);
    setSize(initialSize)
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setSubmittedCorrectionSort(initialSort);
    setReviewAndCorrectSort(initialSort);
    setInventoryRecordsQuery(initialInventoryRecordsQuery);
    setSubmittedCorrectionsQuery(initialSubmittedCorrectionsQuery);
  };

  const handleTabChange = (_event, value) => {
    setCurrentTab(value || BuyerInventoryCorrectionTabKeys.SUBMITTED);
    resetAllFilters();
  };

  const handleSubmitAction = (inventoryId) => {

    const inventory = inventoryRecords.find(item => item.checkInId === inventoryId);
    let ids = [inventory.checkInId];

    if (inventory.checkOutId) {
      ids.push(inventory.checkOutId)
    }

    const formattedInventory = {...inventory, ids: ids}
    setInventoryToCorrect(formattedInventory);
    setModalType(modalTypeOptions.SUBMIT)
  }

  const handleSubmitInventoryCorrection = (body) => {
    submitInventoryCorrection(body)
        .then(() => {
          snackbarShowMessage('Your correction has been submitted for review', "success");
          fetchInventoryRecords();
          fetchSubmittedCorrections();
        })
        .catch(err => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error"));
    handleCloseModal();
  };

  const handleRetractAction = (correctionId) => {
    const correction = rawCorrections.find(item => item.id === correctionId);
    if (correction) {
      setCorrectionToReview(correction);
      setModalType(modalTypeOptions.REVIEW)
    } else {
      snackbarShowMessage('There was an error retrieving this inventory correction', 'error')
    }
  }

  const deleteCorrectionRequest = (correctionId) => {
    deleteInventoryCorrection(correctionId)
      .then(() => {
        snackbarShowMessage("Successfully retracted Inventory Correction.", "success");
        fetchInventoryRecords();
        fetchSubmittedCorrections();
      })
      .catch(err => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error"))
    handleCloseModal();
  }

  useEffect(() => {
    if (account.id) {
      fetchSubmittedCorrections();
      fetchInventoryRecords();
    }
  }, [account, fetchInventoryRecords, fetchSubmittedCorrections]);

  useEffect(() => {
    setInventoryRecordsQuery(prev => ({...prev, page: page, size: size}))
  }, [page, size])

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Inventory Corrections</SecurSpaceReportTitle>
        <TabContextContainer>
          <TabContext value={currentTab}>
            <TabsContainer>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label={'Bookings Tabs'}
                textColor={'secondary'}
                variant={'fullWidth'}
              >
                <Tab value={BuyerInventoryCorrectionTabKeys.SUBMITTED}
                    label={formatTabHeader(BuyerInventoryCorrectionTabs.SUBMITTED)}/>
                <Tab value={BuyerInventoryCorrectionTabKeys.REVIEW}
                    label={formatTabHeader(BuyerInventoryCorrectionTabs.REVIEW)}/>
              </Tabs>
              <BuyerInventoryTabPanel value={BuyerInventoryCorrectionTabKeys.SUBMITTED}>
                <SubmittedCorrectionsTab
                  account={account}
                  rawCorrections={rawCorrections}
                  setCorrectionToReview={setCorrectionToReview}
                  snackbarShowMessage={snackbarShowMessage}
                  handleOpenReviewModal={handleOpenReviewModal}
                  query={submittedCorrectionsQuery}
                  setQuery={setSubmittedCorrectionsQuery}
                  setSort={setSubmittedCorrectionSort}
                  loading={loading}
                />
              </BuyerInventoryTabPanel>
              <BuyerInventoryTabPanel value={BuyerInventoryCorrectionTabKeys.REVIEW}>
                <ReviewAndCorrectTab
                  handleDeleteCorrection={handleRetractAction}
                  handleSubmitCorrection={handleSubmitAction}
                  inventoryRecords={inventoryRecords}
                  setQuery={setInventoryRecordsQuery}
                  initialQuery={initialInventoryRecordsQuery}
                  totalRecords={totalNumberInventoryRecords}
                  page={page}
                  setPage={setPage}
                  size={size}
                  setSize={setSize}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  setSort={setReviewAndCorrectSort}
                  loading={loading}
                />
              </BuyerInventoryTabPanel>
            </TabsContainer>
          </TabContext>
        </TabContextContainer>
      </SecurSpaceReportContainer>
      <ReviewModal
        inventoryCorrection={correctionToReview}
        modalType={modalType}
        handleClose={handleCloseModal}
        openModal={modalType === modalTypeOptions.REVIEW && correctionToReview}
        isSupplier={false}
        onRetract={deleteCorrectionRequest}
      />
      <SubmitCorrectionDialog
        inventory={modalType === modalTypeOptions.SUBMIT ? inventoryToCorrect : null}
        onClose={handleCloseModal}
        submitCorrection={handleSubmitInventoryCorrection}
        snackbarShowMessage={snackbarShowMessage}
      />
    </SecurSpaceContainer>
  )
}

export default withSnackbar(BuyerInventoryCorrections)
